import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import {
  listQuizSchedules,
  createQuizSchedule,
  deleteQuizSchedule,
  patchQuizSchedule,
  getQuizSchedule,
  type QuizScheduleSummary,
  type QuizScheduleDetail,
} from "../../services/quizSchedules";
import { getQuizzes, type Quiz } from "../../services/quizzes";
import { getClubs } from "../../services/api";
import DateInput from "../../components/DateInput";
import { formatDate } from "../../lib/dateFormat";

/**
 * Wizard distribution model.
 * A `slot` is one position in the {week × per_week} grid. We track
 * `manualSlots` to remember the admin's hand-arranged drag-and-drop
 * order. When manualSlots is null we fall back to the auto round-robin.
 */
type Slot = { week_index: number; slot_in_week: number; quiz_id: string };

/**
 * Platform-admin page for recurring quiz drops.
 *
 * UX:
 *   - Top half: list of existing schedules with status pills + small
 *     CTAs (open, pause/resume, delete).
 *   - Right side: a "Programare nouă" button that opens a 1-screen
 *     wizard — pick a quiz pool, set weekly cadence, set duration,
 *     pick target (entire platform OR a specific list of clubs), see
 *     the auto-generated week-by-week timeline before saving.
 *   - Bottom: when a schedule is selected, its full timeline shows up
 *     under the list.
 */
export default function QuizSchedulesPage() {

  const [schedules, setSchedules] = useState<QuizScheduleSummary[]>([]);
  const [loadingList, setLoadingList] = useState(true);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<QuizScheduleDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [allQuizzes, setAllQuizzes] = useState<Quiz[]>([]);
  const [allClubs, setAllClubs] = useState<Array<{ id: string; name: string }>>([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    name: "",
    scope: "platform" as "platform" | "clubs",
    target_club_ids: [] as string[],
    quiz_ids: [] as string[],
    quizzes_per_week: 1,
    start_date: today,
    num_weeks: 4,
  });
  const [creating, setCreating] = useState(false);
  // Manual override of the auto-distribution. When set, the preview
  // and the POST payload use it verbatim. We reset to null on every
  // change that invalidates the previous layout (pool changed, weeks
  // changed, per-week changed).
  const [manualSlots, setManualSlots] = useState<Slot[] | null>(null);
  // HTML5 drag source state — we only ever move one quiz at a time.
  const [dragFrom, setDragFrom] = useState<{ week_index: number; slot_in_week: number } | null>(null);

  const reloadList = async () => {
    try {
      setLoadingList(true);
      const data = await listQuizSchedules();
      setSchedules(data);
    } catch (err: any) {
      console.error("Failed to load quiz schedules:", err);
      toast.error("Nu s-au putut încărca programările");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    reloadList();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingDetail(true);
      try {
        const d = await getQuizSchedule(selectedId);
        if (!cancelled) setDetail(d);
      } catch (err) {
        console.error(err);
        if (!cancelled) toast.error("Nu s-au putut încărca detaliile");
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const openCreate = async () => {
    setShowCreate(true);
    if (optionsLoaded) return;
    try {
      const [qz, clubData] = await Promise.all([
        getQuizzes({ pageSize: 200 }),
        getClubs(),
      ]);
      setAllQuizzes(qz.data || []);
      setAllClubs(clubData.map((c) => c.club));
      setOptionsLoaded(true);
    } catch (err) {
      console.error(err);
      toast.error("Nu s-au putut încărca opțiunile pentru wizard");
    }
  };

  // Once the pool / cadence / weeks change in a way that invalidates
  // the manual layout (different pool of slots), drop the override
  // so the auto-distribution kicks back in.
  useEffect(() => {
    setManualSlots(null);
  }, [form.quiz_ids.join(","), form.num_weeks, form.quizzes_per_week]);

  // Hard cap on weeks: the admin asked for no repeats, so if you've
  // chosen 6 quizzes at 3/week you can't go past 2 weeks (3 × 2 = 6).
  // Float, then clamp to ≥1. We enforce it both on the input itself
  // (the input now reads `max={maxWeeks}`) and on the saved value.
  const maxWeeks = useMemo(() => {
    if (form.quiz_ids.length === 0 || form.quizzes_per_week < 1) return 52;
    return Math.max(1, Math.floor(form.quiz_ids.length / form.quizzes_per_week));
  }, [form.quiz_ids.length, form.quizzes_per_week]);

  // Auto-fold num_weeks if the pool shrinks below the current setting.
  useEffect(() => {
    setForm((f) => (f.num_weeks > maxWeeks ? { ...f, num_weeks: maxWeeks } : f));
  }, [maxWeeks]);

  // Compute the slot grid. Either reads the manual layout or runs
  // the same round-robin the backend does.
  const previewSlots: Slot[] = useMemo(() => {
    if (form.quiz_ids.length === 0 || form.num_weeks < 1 || form.quizzes_per_week < 1) return [];
    if (manualSlots) return manualSlots;
    const result: Slot[] = [];
    let cursor = 0;
    for (let w = 0; w < form.num_weeks; w++) {
      for (let i = 0; i < form.quizzes_per_week; i++) {
        result.push({
          week_index: w,
          slot_in_week: i,
          quiz_id: form.quiz_ids[cursor % form.quiz_ids.length],
        });
        cursor++;
      }
    }
    return result;
  }, [form.quiz_ids, form.num_weeks, form.quizzes_per_week, manualSlots]);

  // Helper for the timeline header: Monday of each week.
  const mondayOfWeek = (w: number): Date => {
    const start = new Date(form.start_date);
    const dow = start.getUTCDay();
    const diff = dow === 0 ? -6 : 1 - dow;
    start.setUTCDate(start.getUTCDate() + diff);
    start.setUTCHours(0, 0, 0, 0);
    start.setUTCDate(start.getUTCDate() + w * 7);
    return start;
  };

  // Drag-and-drop: swap quiz_id between two slots.
  const handleDropOn = (target: { week_index: number; slot_in_week: number }) => {
    if (!dragFrom) return;
    if (dragFrom.week_index === target.week_index && dragFrom.slot_in_week === target.slot_in_week) {
      setDragFrom(null);
      return;
    }
    const slots = [...previewSlots];
    const fromIdx = slots.findIndex(
      (s) => s.week_index === dragFrom.week_index && s.slot_in_week === dragFrom.slot_in_week
    );
    const toIdx = slots.findIndex(
      (s) => s.week_index === target.week_index && s.slot_in_week === target.slot_in_week
    );
    if (fromIdx === -1 || toIdx === -1) {
      setDragFrom(null);
      return;
    }
    const swap = slots[fromIdx].quiz_id;
    slots[fromIdx] = { ...slots[fromIdx], quiz_id: slots[toIdx].quiz_id };
    slots[toIdx] = { ...slots[toIdx], quiz_id: swap };
    setManualSlots(slots);
    setDragFrom(null);
  };

  const quizById = useMemo(() => {
    const map: Record<string, Quiz> = {};
    allQuizzes.forEach((q) => (map[q.id] = q));
    return map;
  }, [allQuizzes]);

  const handleToggleQuiz = (id: string) => {
    setForm((f) => ({
      ...f,
      quiz_ids: f.quiz_ids.includes(id)
        ? f.quiz_ids.filter((x) => x !== id)
        : [...f.quiz_ids, id],
    }));
  };

  const handleToggleClub = (id: string) => {
    setForm((f) => ({
      ...f,
      target_club_ids: f.target_club_ids.includes(id)
        ? f.target_club_ids.filter((x) => x !== id)
        : [...f.target_club_ids, id],
    }));
  };

  const resetForm = () => {
    setForm({
      name: "",
      scope: "platform",
      target_club_ids: [],
      quiz_ids: [],
      quizzes_per_week: 1,
      start_date: today,
      num_weeks: 4,
    });
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error("Dă-i un nume programării");
      return;
    }
    if (form.quiz_ids.length === 0) {
      toast.error("Alege cel puțin un quiz");
      return;
    }
    if (form.scope === "clubs" && form.target_club_ids.length === 0) {
      toast.error("Alege cel puțin un club țintă");
      return;
    }
    setCreating(true);
    try {
      // When the admin has manually rearranged the preview (drag &
      // drop), hand the explicit layout to the backend instead of
      // letting the server-side round-robin recompute it.
      const dropsPayload = manualSlots
        ? manualSlots.map((s) => ({ week_index: s.week_index, quiz_id: s.quiz_id }))
        : undefined;
      await createQuizSchedule({
        name: form.name.trim(),
        scope: form.scope,
        target_club_ids: form.scope === "clubs" ? form.target_club_ids : undefined,
        quiz_ids: form.quiz_ids,
        quizzes_per_week: form.quizzes_per_week,
        start_date: form.start_date,
        num_weeks: form.num_weeks,
        ...(dropsPayload ? { drops: dropsPayload } : {}),
      } as any);
      toast.success("Programare creată");
      setShowCreate(false);
      resetForm();
      await reloadList();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Nu s-a putut crea programarea");
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (s: QuizScheduleSummary) => {
    try {
      await patchQuizSchedule(s.id, { is_active: !s.is_active });
      await reloadList();
      if (detail?.id === s.id) {
        const refreshed = await getQuizSchedule(s.id);
        setDetail(refreshed);
      }
    } catch {
      toast.error("Nu s-a putut schimba statusul");
    }
  };

  const handleDelete = async (s: QuizScheduleSummary) => {
    if (!window.confirm(`Ștergi programarea „${s.name}"?`)) return;
    try {
      await deleteQuizSchedule(s.id);
      if (detail?.id === s.id) setSelectedId(null);
      await reloadList();
      toast.success("Programare ștearsă");
    } catch {
      toast.error("Nu s-a putut șterge programarea");
    }
  };

  // Group the detail's drops by week_index so the timeline can render
  // one row per week with its quiz list.
  const detailDropsByWeek = useMemo(() => {
    if (!detail) return [] as Array<{ week_index: number; week_start: string; quizzes: QuizScheduleDetail["drops"][number]["quiz"][] }>;
    const map = new Map<number, { week_index: number; week_start: string; quizzes: QuizScheduleDetail["drops"][number]["quiz"][] }>();
    detail.drops.forEach((d) => {
      const existing = map.get(d.week_index);
      if (existing) existing.quizzes.push(d.quiz);
      else map.set(d.week_index, { week_index: d.week_index, week_start: d.week_start, quizzes: [d.quiz] });
    });
    return Array.from(map.values()).sort((a, b) => a.week_index - b.week_index);
  }, [detail]);

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-[1500px]">
        <AdminPageHeader
          eyebrow="Conținut și Educație"
          title="Programare quiz-uri"
          subtitle="Aloci un set de quiz-uri pe săptămâni către toți sportivii sau către cluburi specifice. Programarea generează automat un desfășurător săptămânal."
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 7V3m8 4V3M5 11h14M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          }
          actions={
            <AdminButton
              onClick={openCreate}
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
            >
              Programare nouă
            </AdminButton>
          }
        />

        {/* Schedules list */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
          {loadingList ? (
            <div className="p-12 text-center text-slate-500">Se încarcă...</div>
          ) : schedules.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              Nicio programare creată încă. Apasă <span className="font-medium">"Programare nouă"</span>.
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Nume</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Țintă</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Perioadă</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Cadență</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Quiz-uri</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Acțiuni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {schedules.map((s) => (
                  <tr
                    key={s.id}
                    className={`hover:bg-slate-50 cursor-pointer ${
                      selectedId === s.id ? "bg-indigo-50/40" : ""
                    }`}
                    onClick={() => setSelectedId(s.id)}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800">{s.name}</p>
                      <p className="text-xs text-slate-400">creat de {s.created_by.full_name || s.created_by.email}</p>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {s.scope === "platform" ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-emerald-50 text-emerald-700 font-medium">
                          Toate cluburile
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-amber-50 text-amber-700 font-medium">
                          {s.target_club_count} cluburi
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {formatDate(s.start_date)} → {formatDate(s.end_date)}
                      <p className="text-xs text-slate-400">{s.num_weeks} săptămâni</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {s.quizzes_per_week} / săptămână
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {s.picked_quiz_count} în pool · {s.drop_count} drops totale
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`px-2 py-1 text-xs rounded-full font-medium ${
                          s.is_active
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {s.is_active ? "Activă" : "Pauză"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm space-x-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleToggleActive(s)}
                        className="text-indigo-600 hover:underline text-xs"
                      >
                        {s.is_active ? "Pauză" : "Reactivează"}
                      </button>
                      <button
                        onClick={() => handleDelete(s)}
                        className="text-red-600 hover:underline text-xs"
                      >
                        Șterge
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Selected schedule detail / timeline */}
        {selectedId && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">
              Desfășurător săptămânal
            </h2>
            {loadingDetail ? (
              <div className="p-8 text-center text-slate-500">Se încarcă...</div>
            ) : detail ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-sm">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Țintă</p>
                    {detail.scope === "platform" ? (
                      <p className="font-medium text-slate-800">Toate cluburile</p>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {detail.target_clubs.map((c) => (
                          <span
                            key={c.id}
                            className="px-2 py-0.5 text-xs rounded-full bg-amber-50 text-amber-700"
                          >
                            {c.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Pool de quiz-uri</p>
                    <p className="text-slate-700">
                      {detail.picked_quizzes.length} quiz-uri · {detail.quizzes_per_week} pe săptămână ·{" "}
                      {detail.num_weeks} săptămâni
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  {detailDropsByWeek.map((wk) => (
                    <div
                      key={wk.week_index}
                      className="p-4 bg-slate-50 rounded-xl border border-slate-100"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-slate-700">
                          Săptămâna {wk.week_index + 1}
                        </h4>
                        <span className="text-xs text-slate-500">
                          {formatDate(wk.week_start)}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {wk.quizzes.map((q, idx) => (
                          <div
                            key={`${q.id}-${idx}`}
                            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm flex items-center gap-2"
                          >
                            <span className="font-medium text-slate-800">{q.title}</span>
                            <span className="text-xs text-slate-400">· {q.topic}</span>
                            <span className="text-xs text-emerald-600">+{q.reward_points}p</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>

      {/* Create wizard */}
      {showCreate && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 bg-black/50"
          onClick={() => !creating && setShowCreate(false)}
        >
          <div
            className="w-full max-w-3xl bg-white rounded-2xl shadow-xl flex flex-col"
            style={{ maxHeight: "calc(100vh - 2rem)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-800">Programare nouă</h3>
              <button
                onClick={() => !creating && setShowCreate(false)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nume <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="ex: Quiz-uri educație nutrițională Q3"
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Target */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Țintă</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, scope: "platform", target_club_ids: [] })}
                    className={`px-4 py-3 rounded-xl border text-sm font-medium ${
                      form.scope === "platform"
                        ? "bg-emerald-500 text-white border-emerald-500"
                        : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                    }`}
                  >
                    Toate cluburile
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, scope: "clubs" })}
                    className={`px-4 py-3 rounded-xl border text-sm font-medium ${
                      form.scope === "clubs"
                        ? "bg-amber-500 text-white border-amber-500"
                        : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                    }`}
                  >
                    Cluburi specifice
                  </button>
                </div>
                {form.scope === "clubs" && (
                  <div className="mt-3 border border-slate-200 rounded-xl p-3 max-h-48 overflow-y-auto">
                    {allClubs.length === 0 ? (
                      <p className="text-sm text-slate-500">Niciun club disponibil.</p>
                    ) : (
                      <div className="space-y-1">
                        {allClubs.map((c) => (
                          <label
                            key={c.id}
                            className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={form.target_club_ids.includes(c.id)}
                              onChange={() => handleToggleClub(c.id)}
                              className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                            />
                            <span className="text-sm text-slate-700">{c.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Quiz pool */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Pool de quiz-uri <span className="text-red-500">*</span>
                  </label>
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                      form.quiz_ids.length > 0
                        ? "bg-indigo-100 text-indigo-700"
                        : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {form.quiz_ids.length} selectate
                  </span>
                </div>
                <p className="text-xs text-slate-500 mb-2">
                  Selectează quiz-urile din care va trage programarea. Numărul maxim de săptămâni
                  posibil fără repetiție = ⌊selectate / pe săptămână⌋.
                </p>
                <div className="border border-slate-200 rounded-xl p-3 max-h-64 overflow-y-auto">
                  {allQuizzes.length === 0 ? (
                    <p className="text-sm text-slate-500">Niciun quiz activ disponibil.</p>
                  ) : (
                    <div className="space-y-1">
                      {allQuizzes.map((q) => (
                        <label
                          key={q.id}
                          className="flex items-center gap-3 px-2 py-1.5 hover:bg-slate-50 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={form.quiz_ids.includes(q.id)}
                            onChange={() => handleToggleQuiz(q.id)}
                            className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                          />
                          <div className="flex-1">
                            <span className="text-sm font-medium text-slate-800">{q.title}</span>
                            <span className="text-xs text-slate-400 ml-2">· {q.topic}</span>
                          </div>
                          <span className="text-xs text-emerald-600">+{q.reward_points}p</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Cadence */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Quiz-uri pe săptămână
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={14}
                    value={form.quizzes_per_week}
                    onChange={(e) => setForm({ ...form, quizzes_per_week: Math.max(1, Math.min(14, parseInt(e.target.value || "1"))) })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Săptămâni</label>
                  <input
                    type="number"
                    min={1}
                    max={maxWeeks}
                    value={form.num_weeks}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        num_weeks: Math.max(1, Math.min(maxWeeks, parseInt(e.target.value || "1"))),
                      })
                    }
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  {form.quiz_ids.length > 0 && (
                    <p className="text-xs text-slate-500 mt-1">
                      Max fără repetiție: <span className="font-medium">{maxWeeks}</span> săptămâni
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Începe în</label>
                  <DateInput
                    value={form.start_date}
                    onChange={(iso) => setForm({ ...form, start_date: iso })}
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Se va ancora pe luni-ul săptămânii alese.
                  </p>
                </div>
              </div>

              {/* Preview — drag-and-drop. Each card is both draggable
                  and a drop target; drop swaps the two quiz_ids. */}
              {previewSlots.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-slate-700">
                      Desfășurător prevăzut ({previewSlots.length} drops totale)
                    </p>
                    {manualSlots && (
                      <button
                        type="button"
                        onClick={() => setManualSlots(null)}
                        className="text-xs text-indigo-600 hover:underline"
                      >
                        Resetează la auto
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    Trage cardurile între săptămâni ca să rearanjezi manual.
                  </p>
                  <div className="border border-slate-200 rounded-xl p-3 max-h-80 overflow-y-auto space-y-3">
                    {Array.from({ length: form.num_weeks }).map((_, wIdx) => {
                      const weekSlots = previewSlots
                        .filter((s) => s.week_index === wIdx)
                        .sort((a, b) => a.slot_in_week - b.slot_in_week);
                      const wsLabel = formatDate(mondayOfWeek(wIdx).toISOString());
                      return (
                        <div key={wIdx}>
                          <p className="text-xs font-semibold text-slate-700 mb-1.5">
                            Săptămâna {wIdx + 1}{" "}
                            <span className="text-xs text-slate-400 font-normal">({wsLabel})</span>
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {weekSlots.map((slot) => {
                              const q = quizById[slot.quiz_id];
                              const isDragging =
                                dragFrom?.week_index === slot.week_index &&
                                dragFrom?.slot_in_week === slot.slot_in_week;
                              return (
                                <div
                                  key={`${slot.week_index}-${slot.slot_in_week}`}
                                  draggable
                                  onDragStart={() =>
                                    setDragFrom({
                                      week_index: slot.week_index,
                                      slot_in_week: slot.slot_in_week,
                                    })
                                  }
                                  onDragEnd={() => setDragFrom(null)}
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = "move";
                                  }}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    handleDropOn({
                                      week_index: slot.week_index,
                                      slot_in_week: slot.slot_in_week,
                                    });
                                  }}
                                  className={`px-3 py-2 bg-white border rounded-lg text-sm cursor-grab active:cursor-grabbing transition-all ${
                                    isDragging
                                      ? "opacity-40 border-indigo-400 bg-indigo-50"
                                      : "border-slate-200 hover:border-indigo-300 hover:shadow-sm"
                                  }`}
                                  title="Trage pentru a muta"
                                >
                                  <div className="flex items-center gap-2">
                                    <svg
                                      className="w-3 h-3 text-slate-400 flex-shrink-0"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path d="M7 4a1 1 0 100-2 1 1 0 000 2zM13 4a1 1 0 100-2 1 1 0 000 2zM7 11a1 1 0 100-2 1 1 0 000 2zM13 11a1 1 0 100-2 1 1 0 000 2zM7 18a1 1 0 100-2 1 1 0 000 2zM13 18a1 1 0 100-2 1 1 0 000 2z" />
                                    </svg>
                                    <span className="flex-1 truncate font-medium text-slate-800">
                                      {q?.title || slot.quiz_id}
                                    </span>
                                    {q?.reward_points !== undefined && (
                                      <span className="text-xs text-emerald-600 flex-shrink-0">
                                        +{q.reward_points}p
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-100 flex gap-3">
              <button
                type="button"
                onClick={() => !creating && setShowCreate(false)}
                className="flex-1 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200"
              >
                Anulează
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={creating}
                className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 disabled:opacity-50"
              >
                {creating ? "Se creează..." : "Creează programare"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
